'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface PricingOption {
  id: string;
  label: string;
  basePrice: number;
}

interface PricingCalculatorProps {
  serviceType: string;
  options: PricingOption[];
}

export default function PricingCalculator({
  serviceType,
  options
}: PricingCalculatorProps) {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [squareMeters, setSquareMeters] = useState<number>(50);
  const [frequency, setFrequency] = useState<string>('once');
  const [isHydrated, setIsHydrated] = useState(false);

  useState(() => {
    setIsHydrated(true);
  });

  const calculateTotal = () => {
    let total = 0;
    selectedOptions.forEach((optionId) => {
      const option = options.find((opt) => opt.id === optionId);
      if (option) {
        total += option.basePrice;
      }
    });

    const sizeMultiplier = squareMeters / 50;
    total *= sizeMultiplier;

    if (frequency === 'weekly') {
      total *= 0.85;
    } else if (frequency === 'biweekly') {
      total *= 0.9;
    } else if (frequency === 'monthly') {
      total *= 0.95;
    }

    return total.toFixed(2);
  };

  const toggleOption = (optionId: string) => {
    setSelectedOptions((prev) =>
      prev.includes(optionId)
        ? prev.filter((id) => id !== optionId)
        : [...prev, optionId]
    );
  };

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-lg shadow-md p-6">
        <div className="h-64 bg-muted rounded-lg"></div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-md border border-border overflow-hidden">
      <div className="bg-accent text-accent-foreground px-6 py-4">
        <h3 className="text-xl font-heading font-bold flex items-center gap-2">
          <Icon name="CalculatorIcon" size={24} />
          Calculateur de Prix - {serviceType}
        </h3>
      </div>

      <div className="p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Sélectionnez les services:
          </label>
          <div className="space-y-2">
            {options.map((option) => (
              <label
                key={option.id}
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-surface cursor-pointer transition-colors duration-300"
              >
                <input
                  type="checkbox"
                  checked={selectedOptions.includes(option.id)}
                  onChange={() => toggleOption(option.id)}
                  className="w-5 h-5 text-primary border-border rounded focus:ring-2 focus:ring-primary"
                />
                <span className="flex-1 text-sm text-foreground">
                  {option.label}
                </span>
                <span className="text-sm font-semibold text-primary">
                  {option.basePrice} CHF
                </span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Surface (m²): {squareMeters}
          </label>
          <input
            type="range"
            min="20"
            max="200"
            step="10"
            value={squareMeters}
            onChange={(e) => setSquareMeters(Number(e.target.value))}
            className="w-full h-2 bg-surface rounded-lg appearance-none cursor-pointer accent-primary"
          />
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>20 m²</span>
            <span>200 m²</span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Fréquence:
          </label>
          <div className="grid grid-cols-2 gap-2">
            {[
              { value: 'once', label: 'Une fois', discount: '' },
              { value: 'weekly', label: 'Hebdomadaire', discount: '-15%' },
              { value: 'biweekly', label: 'Bimensuel', discount: '-10%' },
              { value: 'monthly', label: 'Mensuel', discount: '-5%' }
            ].map((freq) => (
              <button
                key={freq.value}
                onClick={() => setFrequency(freq.value)}
                className={`p-3 rounded-lg text-sm font-medium transition-all duration-300 ${
                  frequency === freq.value
                    ? 'bg-primary text-primary-foreground shadow-md'
                    : 'bg-surface text-foreground hover:bg-muted'
                }`}
              >
                {freq.label}
                {freq.discount && (
                  <span className="block text-xs mt-1">{freq.discount}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-surface rounded-lg p-4 border-2 border-primary">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">
              Prix estimé:
            </span>
            <span className="text-2xl font-heading font-bold text-primary">
              {calculateTotal()} CHF
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            * Prix indicatif. Devis final après évaluation sur place.
          </p>
        </div>

        <button className="w-full bg-primary hover:bg-secondary text-primary-foreground font-medium py-3 px-4 rounded-lg transition-colors duration-300 flex items-center justify-center gap-2">
          <Icon name="DocumentTextIcon" size={20} />
          Demander un devis détaillé
        </button>
      </div>
    </div>
  );
}